import React, { useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import useOrderRequests from './hooks/useOrderRequests';
import OrderRequestsTable from './components/OrderRequestsTable';
import OrderRequestDetailDialog from './components/OrderRequestDetailDialog';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
    useGetVehicleOrderBrandsQuery,
    useGetVehicleOrderPaymentTermsQuery,
    useLazyExportAllOrderRequestsToExcelQuery
} from '../../../api/endpoints/vehicleOrder';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';
import { usePermissions } from '../../../hooks/usePermissions';
import { Roles } from '../../../utils/roleEnums';

const OrderRequestsPage = () => {
    const { getCurrentLanguageId } = useLanguage();
    const { hasRole } = usePermissions();
    const languageId = getCurrentLanguageId();
    const canViewAllRequests =
        hasRole(Roles.ADMIN) ||
        hasRole(Roles.REGIONAL_MANAGER) ||
        hasRole(Roles.VEHICLE_SALES_MANAGER) ||
        hasRole(Roles.KARSAN_LOGISTICS);

    const {
        th,
        activeTabIndex,
        setActiveTabIndex,
        activeTabKey,
        tabConfig,
        statusCounts,
        orders,
        isLoading,
        refetch
    } = useOrderRequests();

    const { data: brands = [] } = useGetVehicleOrderBrandsQuery({ languageId });
    const { data: paymentTermsList = [] } = useGetVehicleOrderPaymentTermsQuery({ languageId });

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [filteredCounts, setFilteredCounts] = useState({});

    const handleDetailClose = () => {
        setSelectedOrder(null);
    };

    const handleActionComplete = () => {
        setSelectedOrder(null);
        refetch();
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-3">
                <h3>{th('vehicleSalesOrderRequests.pageTitle')}</h3>
                <div className="flex gap-2">
                    <Button
                        icon="pi pi-filter-slash"
                        className="p-button-outlined p-button-secondary"
                        onClick={() => {
                            setFilteredCounts({});
                            setFilterResetKey((key) => key + 1);
                        }}
                        tooltip={th('filter.reset')}
                        tooltipOptions={{ position: 'left' }}
                    />
                    {activeTabIndex === 4 && (
                        <GenericFileExportButton
                            useExportQuery={useLazyExportAllOrderRequestsToExcelQuery}
                            exportParams={{ languageId }}
                            buttonLabel={th('common.export') || "Excel'e Aktar"}
                            buttonClassName="p-button-success"
                            icon="pi pi-file-excel"
                            defaultFileName="AracSiparisTalepleri"
                        />
                    )}
                    <Button
                        icon="pi pi-refresh"
                        label={th('vehicleSalesOrderRequests.buttons.refresh')}
                        onClick={refetch}
                    />
                </div>
            </div>

            <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => setActiveTabIndex(e.index)}
            >
                {tabConfig
                    .filter((tab) => tab.key !== 'all' || canViewAllRequests)
                    .map((tab) => (
                    <TabPanel
                        key={tab.key}
                        header={
                            <span className="flex align-items-center gap-2">
                                <i className={tab.icon}></i>
                                <span>{tab.label}</span>
                                <span className={`px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>
                                    {filteredCounts[tab.key] ?? statusCounts[tab.key] ?? 0}
                                </span>
                            </span>
                        }
                    >
                        <OrderRequestsTable
                            key={`${tab.key}-${filterResetKey}`}
                            data={orders}
                            loading={isLoading}
                            activeTabKey={tab.key}
                            onFilteredCountChange={(count) =>
                                setFilteredCounts((current) => {
                                    if (count == null) {
                                        const next = { ...current };
                                        delete next[tab.key];
                                        return next;
                                    }
                                    return { ...current, [tab.key]: count };
                                })
                            }
                            onView={(row) => setSelectedOrder(row)}
                        />
                    </TabPanel>
                ))}
            </TabView>

            {selectedOrder && (
                <OrderRequestDetailDialog
                    visible={!!selectedOrder}
                    order={selectedOrder}
                    activeTabKey={activeTabKey}
                    onHide={handleDetailClose}
                    onActionComplete={handleActionComplete}
                    brands={brands}
                    paymentTermsList={paymentTermsList}
                />
            )}
        </div>
    );
};

export default OrderRequestsPage;
